import type { ReactElement } from 'react';
import { memo } from 'react';

import type { OrderState } from '@buying/library';

import { CopyTooltip } from '@bestseller-bit/frontend-community.components.copy-tooltip';
import {
    CircleInfoIcon,
    scrollbarGutter,
    useIsLargeScreen,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import { fullDateFormat, OrderStateComponent } from '@buying/library';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';

interface OrderInfoHeaderProps {
    /** The order state to render (order or sample order) */
    state: Omit<OrderState, 'isDeleted'>;
    /** The code shown under the state; copyable on click */
    orderCode: string;
    /** Name of the user who created the order, already resolved with a fallback */
    createdByName: string;
    /** Date of the creation, already resolved with a fallback */
    createdDate: string | Date;
    /** Name of the user who last edited the order */
    lastEditedByName?: string | null;
    /** Date of the last edit */
    lastEditedDate?: string | Date | null;
}

/**
 * Shared header for the order info panel used by the order and sample order cards.
 * Purely presentational — the caller resolves the values and renders it in the `infoHeader` grid area.
 */
const OrderInfoHeader = memo(
    ({
        state,
        orderCode,
        createdByName,
        createdDate,
        lastEditedByName,
        lastEditedDate,
    }: OrderInfoHeaderProps): ReactElement => {
        const { palette } = useTheme();
        const isLargeScreen = useIsLargeScreen();

        const latestEdit =
            lastEditedByName && lastEditedDate
                ? { userName: lastEditedByName, date: lastEditedDate }
                : null;

        const displayUserName = latestEdit?.userName ?? createdByName;
        const displayDate = latestEdit?.date ?? createdDate;

        const displayDateFormatted = format(
            new Date(displayDate),
            fullDateFormat
        );

        const createdDateFormatted = format(
            new Date(createdDate),
            fullDateFormat
        );

        const contentTooltip = latestEdit ? (
            <>
                Latest edit
                <br />
                <br />
                Order created by:
                <br />
                {createdByName}
                <br />
                {createdDateFormatted}
            </>
        ) : (
            'Order created by, no edits made yet'
        );

        return (
            <Box
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingX: { xs: 5, xl: 10 },
                    paddingY: 3,
                    borderLeft: `1px solid ${palette.divider}`,
                    borderBottom: `1px solid ${palette.divider}`,
                    backgroundColor: 'background.secondary',
                    display: 'grid',
                    gridArea: 'infoHeader',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', // Two column layout
                    columnGap: { xs: 5, xl: 10 },
                    ...scrollbarGutter(),
                }}
            >
                <Stack>
                    <OrderStateComponent state={state} />

                    <CopyTooltip placement="left">
                        {({ copyValue }): ReactElement => (
                            <Typography
                                variant={
                                    isLargeScreen
                                        ? 'bodySmall.highlight'
                                        : 'captions.highlight'
                                }
                                onClick={() => {
                                    void copyValue(orderCode);
                                }}
                            >
                                {orderCode}
                            </Typography>
                        )}
                    </CopyTooltip>
                </Stack>

                <Stack
                    direction="row"
                    spacing={3}
                    sx={{
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                    useFlexGap
                >
                    <Stack sx={{ textAlign: 'right' }}>
                        <Typography
                            variant={
                                isLargeScreen
                                    ? 'captions.default'
                                    : 'captionsSmall.default'
                            }
                            sx={{ textWrap: 'balance' }} // Long names can break oddly; this reads better
                        >
                            {displayUserName}
                        </Typography>

                        <Typography
                            variant={
                                isLargeScreen
                                    ? 'bodySmall.default'
                                    : 'captions.default'
                            }
                        >
                            {displayDateFormatted}
                        </Typography>
                    </Stack>

                    <Tooltip
                        title={contentTooltip}
                        placement="top"
                        leaveDelay={200}
                    >
                        <CircleInfoIcon
                            color={alpha(palette.primary.main, 0.5)}
                            size="16"
                            style={{ flexShrink: 0 }}
                        />
                    </Tooltip>
                </Stack>
            </Box>
        );
    }
);

OrderInfoHeader.displayName = 'OrderInfoHeader';

export { OrderInfoHeader };
export type { OrderInfoHeaderProps };
